import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type { User } from '@keboola/api-client/developerPortal/types';
import {
  AlertModal,
  Badge,
  Button,
  EmptyState,
  FormGroup,
  Label,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Select,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';

import { useAuth } from '../../auth/context';
import { QueryError } from '../../components/QueryError';
import { ManageUserVendorsModal } from '../../features/admin/components/ManageUserVendorsModal';
import {
  useAdminUsers,
  useAdminVendors,
  useDisableMfa,
  useMakeUserAdmin,
} from '../../features/admin/queries';
import {
  canOfferAdminGrant,
  filterAdminUsers,
  PENDING_VENDORS_OPTION,
  vendorNameById,
} from '../../features/admin/users';
import { getApiErrorMessage } from '../../features/auth/errors';
import { isSelf } from '../../features/vendors/members';

export const Route = createFileRoute('/admin/developers')({
  component: AdminDevelopersPage,
  head: () => ({ meta: [{ title: 'Developers | Keboola Developer Portal admin' }] }),
});

function AdminDevelopersPage() {
  const { identity } = useAuth();
  const callerEmail = identity?.email ?? null;
  const users = useAdminUsers();
  const vendors = useAdminVendors();
  const disableMfa = useDisableMfa();
  const makeAdmin = useMakeUserAdmin();

  const [query, setQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');

  // Waits on both queries: without vendors, the filter, the membership names and the
  // "pending vendors" match (an id with no record) all silently break.
  const isLoading = users.isLoading || vendors.isLoading;
  const isError = users.isError || vendors.isError;
  const error = users.error ?? vendors.error;
  const retry = () => {
    void users.refetch();
    void vendors.refetch();
  };

  const vendorOptions = useMemo(
    () => [
      { value: '', label: 'All vendors' },
      // Client-only mode: finds orphaned memberships, not vendors awaiting approval.
      { value: PENDING_VENDORS_OPTION, label: 'All pending vendors' },
      ...(vendors.data ?? []).map((vendor) => ({ value: vendor.id, label: vendor.name })),
    ],
    [vendors.data],
  );

  const visible = useMemo(
    () =>
      filterAdminUsers(users.data ?? [], vendors.data ?? [], {
        query,
        vendor: vendorFilter,
      }),
    [users.data, vendors.data, query, vendorFilter],
  );

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Developers"
          description="Everyone with a Developer Portal account."
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={isError}
        hasData={users.data !== undefined && vendors.data !== undefined}
        error={error}
        onRetry={retry}
        loadTitle="Could not load the developers"
        refreshTitle="Could not refresh the developers"
        staleNote="Showing the developers from the last successful load."
        titleAs="h2"
      />

      {users.data !== undefined && vendors.data !== undefined && (
        <>
          <PageToolbar>
            {/* Labelled like the filter beside it so `items-center` aligns their baselines. */}
            <PageToolbar.Search>
              <FormGroup className="tw:w-full">
                <Label htmlFor="admin-developer-search">Search</Label>
                <SearchInput
                  id="admin-developer-search"
                  allowClear
                  placeholder="Search by name or email"
                  value={query}
                  onValueChange={setQuery}
                />
              </FormGroup>
            </PageToolbar.Search>
            <FormGroup className="tw:w-52">
              <Label htmlFor="admin-vendor-filter">Vendor</Label>
              <Select
                id="admin-vendor-filter"
                items={vendorOptions}
                value={vendorFilter}
                onValueChange={setVendorFilter}
              />
            </FormGroup>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>No developer matches these filters.</EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Developer</Table.Head>
                  <Table.Head>Vendors</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((user) => (
                  <Table.Row key={user.username}>
                    <Table.Cell>
                      <UserCell user={user} isSelf={isSelf(user, callerEmail)} />
                    </Table.Cell>
                    <Table.Cell>
                      {/* `items-center`: the pencil sits beside the whole list, not its first line. */}
                      <div className="tw:flex tw:items-center tw:gap-1">
                        <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-0.5 tw:p-0 tw:text-sm">
                          {(user.vendors ?? []).length === 0 && (
                            <li className="tw:text-muted-foreground">None</li>
                          )}
                          {(user.vendors ?? []).map((id) => (
                            // `break-all`: a pending id has no break opportunities.
                            <li key={id} className="tw:break-all">
                              {vendorNameById(id, vendors.data ?? [])}
                            </li>
                          ))}
                        </ul>
                        <ManageUserVendorsModal user={user} vendors={vendors.data ?? []} />
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:justify-end tw:gap-2">
                        <AlertModal
                          // Caught: `AlertModal` closes on settle, so a rejection would vanish silently.
                          onConfirm={async () => {
                            try {
                              await disableMfa.mutateAsync(user.username);
                              toast.success(`MFA was cleared for ${user.username}.`);
                            } catch (error) {
                              toast.error(
                                getApiErrorMessage(
                                  error,
                                  `Could not clear MFA for ${user.username}.`,
                                ),
                              );
                            }
                          }}
                        >
                          <AlertModal.Trigger asChild>
                            <Button variant="secondary" size="small">
                              Disable MFA
                            </Button>
                          </AlertModal.Trigger>
                          <AlertModal.Content
                            variant="danger"
                            title={`Clear MFA for ${user.username}?`}
                            confirmLabel="Disable MFA"
                          >
                            They will be able to sign in without a second factor and will be asked
                            to enrol a new device. Only do this after verifying who is asking.
                          </AlertModal.Content>
                        </AlertModal>

                        {canOfferAdminGrant(user, callerEmail) && (
                          <AlertModal
                            onConfirm={async () => {
                              try {
                                await makeAdmin.mutateAsync(user.username);
                                toast.success(`${user.username} is now an admin.`);
                              } catch (error) {
                                toast.error(
                                  getApiErrorMessage(
                                    error,
                                    `Could not grant admin to ${user.username}.`,
                                  ),
                                );
                              }
                            }}
                          >
                            <AlertModal.Trigger asChild>
                              <Button variant="secondary" size="small">
                                Make admin
                              </Button>
                            </AlertModal.Trigger>
                            <AlertModal.Content
                              variant="danger"
                              title={`Make ${user.username} an admin?`}
                              confirmLabel="Grant admin"
                            >
                              They will be able to edit, publish and delete every component of every
                              vendor, and grant admin to others. This screen cannot revoke it; that
                              takes a call to the apps-api.
                            </AlertModal.Content>
                          </AlertModal>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}

function UserCell({ user, isSelf }: { user: User; isSelf: boolean }) {
  const createdOn = user.createdOn;

  return (
    <div className="tw:flex tw:flex-col">
      <span className="tw:flex tw:items-center tw:gap-2 tw:font-medium">
        {user.name ?? user.username}
        {isSelf && <Badge color="purple-inverse">You</Badge>}
      </span>
      {user.name !== undefined && user.name !== null && (
        <span className="tw:text-xs tw:text-muted-foreground">{user.username}</span>
      )}
      {createdOn !== undefined && (
        <span className="tw:text-xs tw:text-muted-foreground">
          joined {new Date(createdOn).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}
